import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Base } from '@itsrever/design-system'
import {
  GroupNangoConnection,
  connectionLabel,
  integrationKeyOf,
  platformOf
} from '@/domain/nango-connection'
import { NANGO_URL } from '@/application/manage-nango-connections'
import {
  isWarehousesV2Enabled,
  useFeatureFlag
} from '@/application/use-feature-flag'
import axerveLogo from '@/assets/images/integrations/axerve.svg'
import commercelayerLogo from '@/assets/images/integrations/commercelayer.svg'
import klarnaLogo from '@/assets/images/integrations/klarna.svg'
import logicommerceLogo from '@/assets/images/integrations/logicommerce.svg'
import magentoLogo from '@/assets/images/integrations/magento.svg'
import miraklLogo from '@/assets/images/integrations/mirakl.svg'
import prestashopLogo from '@/assets/images/integrations/prestashop.svg'
import redsysLogo from '@/assets/images/integrations/redsys.svg'
import reverLogo from '@/assets/images/integrations/rever.svg'
import salesforceLogo from '@/assets/images/integrations/salesforce.svg'
import sequraLogo from '@/assets/images/integrations/sequra.svg'
import shopwareLogo from '@/assets/images/integrations/shopware.svg'
import tiktokLogo from '@/assets/images/integrations/tiktok.svg'
import { INTEGRATIONS_PATH } from './hub-paths'

// Keyed by platform: Nango has no logo of its own for our templates (rever-*) and generic
// providers, its Shopware one is black and its Magento one is Adobe's.
const OWN_LOGOS: Record<string, string> = {
  prestashop: prestashopLogo,
  custom: reverLogo,
  magento: magentoLogo,
  salesforce: salesforceLogo,
  shopware: shopwareLogo,
  commercelayer: commercelayerLogo,
  logicommerce: logicommerceLogo,
  mirakl: miraklLogo,
  'tiktok-shop': tiktokLogo,
  klarna: klarnaLogo,
  axerve: axerveLogo,
  redsys: redsysLogo,
  sequra: sequraLogo
}

// Values are plain text React escapes: i18next's own escaping would show a date's "/" as "&#x2F;".
export const RAW_TEXT = { interpolation: { escapeValue: false } }

const INTEGRATION_NAMES: Record<string, string> = {
  shopify: 'Shopify',
  woocommerce: 'WooCommerce',
  prestashop: 'PrestaShop',
  'prestashop-query': 'PrestaShop (query key)',
  shopware: 'Shopware',
  'custom-v2': 'Custom v2',
  klarna: 'Klarna',
  adyen: 'Adyen',
  axerve: 'Axerve',
  'boggi-oms': 'Boggi OMS',
  marypaz: 'MaryPaz OMS',
  'alvaromoreno-erp': 'Álvaro Moreno ERP',
  magento: 'Magento',
  'magento-bearer': 'Magento',
  'magento-oauth1': 'Magento (OAuth 1)',
  'custom-v1': 'Custom v1',
  'custom-v1-rever-key': 'Custom v1 (REVER key)',
  custom: 'Custom integration',
  commercelayer: 'Commerce Layer',
  'salesforce-scapi': 'Salesforce SCAPI',
  'salesforce-slas': 'Salesforce SLAS',
  'salesforce-core': 'Salesforce Core',
  'salesforce-ocapi': 'Salesforce OCAPI',
  'salesforce-ocapi-agent': 'Salesforce OCAPI (access key)',
  'salesforce-ocapi-basic': 'Salesforce OCAPI (second client)',
  vtex: 'VTEX',
  logicommerce: 'Logicommerce',
  'own-platform': 'Own platform',
  mirakl: 'Mirakl',
  'tiktok-shop': 'TikTok Shop',
  paypal: 'PayPal',
  redsys: 'Redsys',
  sequra: 'seQura',
  'marypaz-erp': 'MaryPaz ERP',
  'doppelganger-erp': 'Doppelganger ERP'
}

// Our integrations are named by their key: the template they sit on (rever-*) names none of them.
export function providerName(
  connection: Pick<GroupNangoConnection, 'integration' | 'provider'>
) {
  const key = integrationKeyOf(connection)
  return (
    INTEGRATION_NAMES[connection.integration] ??
    INTEGRATION_NAMES[key] ??
    key.charAt(0).toUpperCase() + key.slice(1)
  )
}

// Nango gives each of our templates (rever-*) a borrowed logo, often the generic or another
// platform's, so an integration on one with no logo of ours shows its initial.
export const providerOf = (
  connection: Pick<GroupNangoConnection, 'integration' | 'provider'>
) => {
  const platform = platformOf(connection.integration)
  return Object.hasOwn(OWN_LOGOS, platform) ||
    connection.provider.startsWith('rever-')
    ? platform
    : integrationKeyOf(connection)
}

// Nango serves its catalogue's logos; for a provider without one it answers HTML, so the
// image fails and the initial shows instead.
export const ProviderLogo = ({
  provider,
  name,
  src
}: {
  provider: string
  name: string
  src?: string
}) => {
  const [failed, setFailed] = useState(false)
  return (
    <Base.Thumbnail
      alt={name}
      src={
        failed
          ? undefined
          : (src ??
            OWN_LOGOS[provider] ??
            `${NANGO_URL}/images/template-logos/${encodeURIComponent(provider)}.svg`)
      }
      onError={() => setFailed(true)}
      className="object-contain"
    />
  )
}

export const BackToIntegrations = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { search } = useLocation()
  return (
    <div className="px-7 pt-3">
      <Base.Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => navigate(`${INTEGRATIONS_PATH}${search}`)}
      >
        {t('integrations_hub.back')}
      </Base.Button>
    </div>
  )
}

// Stores outside the group's active ones are left out: they cannot pick a connection here.
export function useUsedBySummary() {
  const { t } = useTranslation()
  return (linked: string[], groupStores: string[]) => {
    const active = groupStores.filter((store) => linked.includes(store))
    if (active.length === 0) return t('integrations_hub.used_by_none')
    if (groupStores.length > 1 && active.length === groupStores.length) {
      return t('integrations_hub.used_by_all', { count: active.length })
    }
    return t('integrations_hub.used_by', {
      stores: active.join(', '),
      ...RAW_TEXT
    })
  }
}

// Same gate as Configuration > Warehouses: until a group moves to group-level warehouses, its
// group list is not what its portals use.
export function useGroupWarehousesEnabled(group?: string) {
  const { isFlagEnabled, isFlagLoading } = useFeatureFlag(
    isWarehousesV2Enabled,
    { group }
  )
  return {
    warehousesEnabled: !!group && isFlagEnabled,
    loadingWarehousesFlag: !!group && isFlagLoading
  }
}

// Why a connection stopped working when its portals call another store than the one it signs in to now.
export function useOtherStoreCopy() {
  const { t } = useTranslation()
  return (connection: GroupNangoConnection) =>
    connection.health?.portalStore
      ? t('integrations_hub.attention.other_store', {
          connection: connectionLabel(connection),
          portalStore: connection.health.portalStore,
          ...RAW_TEXT
        })
      : undefined
}

// What the hub says about a connection the store stopped accepting, dated when known. On the connection's own row
// the name is already above, so `onRow` leaves it out.
export function useStoppedCopy() {
  const { t, i18n } = useTranslation()
  return (connection: GroupNangoConnection, { onRow = false } = {}) => {
    const values = {
      name: providerName(connection),
      connection: connectionLabel(connection),
      ...RAW_TEXT
    }
    const key = `integrations_hub.attention.${onRow ? 'stopped_on' : 'stopped'}`
    const since = connection.health?.failingSince
    return since
      ? t(key, { ...values, date: since.toLocaleDateString(i18n.language) })
      : t(`${key}_undated`, values)
  }
}
